import type { ReactNode } from 'react'
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'

type PaginationPage = number | 'ellipsis'

interface PaginationProps {
  summary?: ReactNode
  ariaLabel?: string
  pages?: PaginationPage[]
  totalPages?: number
  currentPage?: number
  className?: string
  navClassName?: string
  buttonClassName?: string
  activeButtonClassName?: string
  inactiveButtonClassName?: string
  showPageNumbers?: boolean
  previousDisabled?: boolean
  nextDisabled?: boolean
  onPrevious?: () => void
  onNext?: () => void
  onPageChange?: (page: number) => void

  // Page Size selector props
  pageSize?: number
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]

  // Optional Sort selector props
  sortBy?: string
  onSortByChange?: (sort: string) => void
  sortByOptions?: Array<{ label: string; value: string }>
}

function generatePageNumbers(currentPage: number, totalPages?: number, customPages?: PaginationPage[]): PaginationPage[] {
  if (customPages && customPages.length > 0 && customPages.length <= 7) {
    return customPages
  }

  const total = totalPages || (customPages ? customPages.filter((p): p is number => typeof p === 'number').pop() || 1 : 1)
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const result: PaginationPage[] = [1]

  if (currentPage > 3) {
    result.push('ellipsis')
  }

  const start = Math.max(2, currentPage - 1)
  const end = Math.min(total - 1, currentPage + 1)

  for (let i = start; i <= end; i++) {
    result.push(i)
  }

  if (currentPage < total - 2) {
    result.push('ellipsis')
  }

  if (total > 1) {
    result.push(total)
  }

  return result
}

export function generateSmartPages(current: number, total: number): PaginationPage[] {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  if (current <= 2) {
    return [1, 2, 'ellipsis', total]
  }
  if (current >= total - 1) {
    return [1, 'ellipsis', total - 1, total]
  }
  return [1, 'ellipsis', current, 'ellipsis', total]
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export function Pagination({
  summary,
  ariaLabel = 'Pagination Navigation',
  pages,
  totalPages = 1,
  currentPage = 1,
  className = '',
  navClassName = '',
  buttonClassName = '',
  activeButtonClassName = '',
  inactiveButtonClassName = '',
  showPageNumbers = true,
  previousDisabled,
  nextDisabled,
  onPrevious,
  onNext,
  onPageChange,
  pageSize = 10,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  sortBy,
  onSortByChange,
  sortByOptions = [],
}: PaginationProps) {
  const baseButtonClassName =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#E2E8F0] bg-white text-[#475569] transition hover:border-[#005156] hover:text-[#005156] hover:bg-[#E6F4F1] disabled:cursor-not-allowed disabled:opacity-40 shadow-xs'

  const pageList = generatePageNumbers(currentPage, totalPages, pages)
  const isPrevDisabled = previousDisabled ?? currentPage <= 1
  const isNextDisabled = nextDisabled ?? (totalPages ? currentPage >= totalPages : false)

  const handlePrev = () => {
    if (onPrevious) onPrevious()
    else if (onPageChange && currentPage > 1) onPageChange(currentPage - 1)
  }

  const handleNext = () => {
    if (onNext) onNext()
    else if (onPageChange && currentPage < totalPages) onPageChange(currentPage + 1)
  }

  const effectivePageSizeOptions = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : Array.from(new Set([pageSize, ...pageSizeOptions])).sort((a, b) => a - b)

  return (
    <footer
      className={`flex flex-col gap-4 font-sans sm:flex-row sm:items-center sm:justify-between min-w-0 w-full pt-4 border-t border-[#E2E8F0] ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between sm:justify-start gap-x-6 gap-y-2 min-w-0 w-full sm:w-auto">
        {summary ? (
          <div className="text-xs sm:text-sm font-medium text-slate-600 leading-normal">
            {summary}
          </div>
        ) : null}

        {/* Page Size selector */}
        {onPageSizeChange ? (
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-[#64748B]">Items per page:</span>
            <div className="relative inline-flex items-center">
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
                className="w-[72px] h-8 appearance-none pl-3 pr-7 border border-[#E2E8F0] rounded-lg bg-white text-xs font-bold text-[#0F172A] cursor-pointer focus:outline-none focus:border-[#005156] focus:ring-1 focus:ring-[#005156] shadow-xs"
              >
                {effectivePageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 pointer-events-none h-4 w-4 text-[#64748B]" />
            </div>
          </div>
        ) : null}

        {/* Optional Sort By selector */}
        {onSortByChange && sortByOptions.length > 0 ? (
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-[#64748B]">Sort by:</span>
            <div className="relative inline-flex items-center">
              <select
                value={sortBy}
                onChange={(e) => onSortByChange?.(e.target.value)}
                className="h-8 appearance-none pl-3 pr-8 border border-[#E2E8F0] rounded-lg bg-white text-xs font-bold text-[#0F172A] cursor-pointer focus:outline-none focus:border-[#005156] focus:ring-1 focus:ring-[#005156] shadow-xs"
              >
                {sortByOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 pointer-events-none h-4 w-4 text-[#64748B]" />
            </div>
          </div>
        ) : null}
      </div>

      <nav
        className={`flex items-center justify-center sm:justify-end gap-1.5 min-w-0 w-full sm:w-auto ${navClassName}`}
        aria-label={ariaLabel}
      >
        {/* Previous Button */}
        <button
          type="button"
          onClick={handlePrev}
          disabled={isPrevDisabled}
          className={
            buttonClassName
              ? `inline-flex items-center justify-center transition disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName} ${
                  inactiveButtonClassName || 'border border-[#E2E8F0] bg-white text-[#475569]'
                }`
              : baseButtonClassName
          }
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Page Numbers */}
        {showPageNumbers && pageList.length > 0 ? (
          <div className="flex flex-wrap items-center justify-center gap-1 min-w-0">
            {pageList.map((p, index) => {
              if (p === 'ellipsis') {
                return (
                  <span key={`ellipsis-${index}`} className="px-1 text-xs font-bold text-[#94A3B8]">
                    ...
                  </span>
                )
              }

              const isCurrent = p === currentPage
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange?.(p)}
                  className={`inline-flex h-8 min-w-[32px] px-2.5 items-center justify-center rounded-lg text-xs font-bold transition cursor-pointer ${
                    isCurrent
                      ? activeButtonClassName || 'bg-[#146971] text-white shadow-xs'
                      : inactiveButtonClassName ||
                        'border border-[#E2E8F0] bg-white text-[#475569] hover:bg-[#E6F4F1] hover:text-[#146971] hover:border-[#146971]'
                  } ${buttonClassName || ''}`}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {p}
                </button>
              )
            })}
          </div>
        ) : null}

        {/* Next Button */}
        <button
          type="button"
          onClick={handleNext}
          disabled={isNextDisabled}
          className={
            buttonClassName
              ? `inline-flex items-center justify-center transition disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName} ${
                  inactiveButtonClassName || 'border border-[#E2E8F0] bg-white text-[#475569]'
                }`
              : baseButtonClassName
          }
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </nav>
    </footer>
  )
}

export default Pagination
